import type { VideoProps } from 'fitify-ui-onboarding/src/types/ui'
import { useCallback } from 'react'

import { useLayoutTheme } from 'components/GenderFunnel/Layout/useLayoutTheme'
import { DEFAULT_VIDEO_VARIANT } from 'data/videos'
import type {
  VideoCategories,
  VideoCategoryPageAssets,
  VideoCategoryPages,
  VideoCategoryVariants,
} from 'types/videos'
import { useDataState } from 'utils/state'
import { getVideoProps } from 'utils/videos'

export const useCategoryVideoVariant = <TCategory extends VideoCategories>(
  category?: TCategory
): string => {
  const { data } = useDataState()
  const theme = useLayoutTheme()

  switch (category) {
    case 'm01': {
      if (!data.common.onboardingVariant) {
        if (theme.isDarkTheme) {
          return 'dark'
        }
      }
      return DEFAULT_VIDEO_VARIANT
    }
    default:
      return DEFAULT_VIDEO_VARIANT
  }
}

export const useFunnelVideos = <TCategory extends VideoCategories>(
  funnel: TCategory
) => {
  const variant = useCategoryVideoVariant(funnel)

  const getFunnelVideoProps = useCallback(
    <
      TVariant extends VideoCategoryVariants<TCategory>,
      TPages extends VideoCategoryPages<TCategory, TVariant>,
      TVideo extends VideoCategoryPageAssets<TCategory, TVariant, TPages>,
    >(
      page: TPages,
      videoName: TVideo
    ): VideoProps =>
      getVideoProps(funnel, page, videoName, {
        variant: variant as TVariant,
      }),
    [funnel, variant]
  )

  return { getFunnelVideoProps }
}
